import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
    Sparkles, Save, Loader2, Layers, Ruler, Flag,
    Heart, Zap, Repeat, Sparkle, HelpCircle, Move3d, Wand2,
    Shirt, MoveHorizontal, Grip, StretchHorizontal, Code2, PackageCheck,
} from "lucide-react";
import { PageHeader, PrimaryButton, Card, CardHeader } from "@/components/design-system";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fetchAllWidgetSettings, updateWidgetSettings } from "@/lib/widget-settings-api";
import { WizardPreviewProvider, WizardPreviewModal, WizardPreviewNavigator } from "@/components/find-my-size/WizardPreview";

/**
 * Bedenimi Bul Sihirbazı — Merchant config sayfası
 *
 * ÜST DÜZEY YAPI: 2 üst tab — Beden Tablosu (SizeChartModal) ile birebir senkron:
 *  • Görünüm & Yerleşim — Modal/Drawer + açılış kartı
 *  • İçerik            — 3 ana Accordion (multiple, hepsi kapalı start):
 *      1. Karşılama    — Akıllı Beden Önerisi toggle + fastPath + nested Tam/Kısmi
 *      2. Ölçüler      — Ortak ipucu + nested Temel/Fit/Göğüs/Bel/Kalça/Omuz
 *      3. Final        — Sonuç accordion + Nazik Ret toggle + nested ret accordion
 *
 * "Davranış" tab kaldırıldı — Smart Recall Karşılama'ya, Nazik Ret Final'a taşındı.
 */

const STORAGE_KEY = "sayz.experience.find-my-size.v1";
const DEFAULT_TITLE = "Bedenimi Bul";

/* Sayz Widget ile birebir tab styling — SizeChartModal ile senkron */
const TAB_LIST_CLS =
    "flex w-full items-center bg-white rounded-lg p-1 h-10 gap-1";
const TAB_TRIGGER_CLS =
    "group relative flex-1 inline-flex items-center justify-center gap-2 h-8 px-3 rounded-lg text-[12.5px] font-medium transition-all border border-transparent " +
    "text-muted-foreground hover:text-foreground data-[state=inactive]:hover:bg-muted/60 " +
    "data-[state=active]:bg-foreground data-[state=active]:border-foreground data-[state=active]:text-background data-[state=active]:shadow-sm " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20 focus-visible:ring-offset-0";

const DEFAULTS = {
    // Davranış — uygun beden yoksa ne yapmalı?
    // "closest"      → varsayılan (En Yakın Bedeni Öner). UI'de gösterilmez, Nazik Ret toggle OFF durumu.
    // "kind_refusal" → Nazik Ret toggle ON durumu.
    outOfRangeStrategy: "kind_refusal",
    // Smart Recall — geri dönen ziyaretçi deneyimi
    smartRecall: {
        enabled: true,                    // Master toggle — kapatılırsa karşılama tamamen atlanır
        fastPathMode: "auto_recommend",   // "auto_recommend" | "always_confirm"
    },
    // İçerik metinleri (opsiyonel override)
    stepCopy: {
        welcomeLabel: "Merhaba Tekrar",
        welcomeTitle: "Seni hatırlıyorum",
        welcomeDesc: "Bildiğim bilgileri atlayıp doğrudan öneriye geçebilirim, ya da bilgilerini değiştirebilirsin.",
        welcomeChange: "Bilgilerimi Değiştir",
        welcomeContinueButton: "Bedeni Seç",
        welcomeInfoText: "Bilgilerin aynıysa bu beden sana uygun.",
        // Partial (kısmi profil) copy
        welcomePartialLabel: "Neredeyse Hazırız",
        welcomePartialTitle: "Bir şey daha soracağım",
        welcomePartialDesc: "Bu ürün için birkaç ölçünü daha bilmem gerekiyor. Sonra hemen bedenini bulacağım.",
        // Always-confirm copy
        welcomeConfirmLabel: "Merhaba Tekrar",
        welcomeConfirmTitle: "Bilgilerin hâlâ doğru mu?",
        welcomeConfirmDesc: "Bu bilgileri en son verdiğinden bu yana bir değişiklik yoksa, direkt önerimi göstereyim.",
        welcomeConfirmButton: "Evet, önerimi göster",
        basicTitle: "Seni tanıyalım",
        basicDesc: "Boy, kilo ve yaşın en doğru bedeni bulmak için yeterli. Ölçü sormuyoruz.",
        fitTitle: "Bu ürünü nasıl giymek istersin?",
        fitDesc: "Fit tercihin, aynı vücut için farklı beden önerir. Beğendiğini seç.",
        bodyPrompts: {
            chest: "Göğüs yapını en iyi hangisi tanımlıyor?",
            waist: "Bel/karın yapını en iyi hangisi tanımlıyor?",
            hip: "Kalça yapını en iyi hangisi tanımlıyor?",
            shoulder: "Omuz yapını en iyi hangisi tanımlıyor?",
        },
        // Her siluet ekranı için ayrı ipucu — WizardPreview `bodyHints?.[kind]`
        // önce, sonra legacy `bodyHint`, sonra default fallback.
        bodyHints: {
            chest: "Göğsünü değil, silüetini seç — mükemmel olmasına gerek yok.",
            waist: "Beliyle karnını en iyi tarif eden silüeti seç.",
            hip: "Kalçana en yakın silüeti seç — otururken görünen değil.",
            shoulder: "Omzuna en yakın silüeti seç — jilet keskinliğe gerek yok.",
        },
    },
    resultCopy: {
        resultLabel: "Önerimiz",
        resultText: "Vücut bilgilerin ve fit tercihine göre bu ürün için önerdiğimiz beden.",
        refusalLabel: "Emin değiliz",
        refusalText: "Bu ürün için sana ideal bedeni tam olarak bulamadık. Yanlış bedenle iade yaşamamak için beden tablosunu incelemeni öneririm.",
        selectSizeButton: "Bedeni Seç",
        sizeChartButton: "Beden Tablosunu Gör",
    },
    // Site Entegrasyonu — merchant'ın DOM yapısını tanıtan ayarlar. "Bedeni Seç"
    // tıklandığında widget host page'deki ilgili beden butonunu bulup otomatik
    // tıklar. Stokta olmayan bedenleri de bu ayarlar üzerinden algılar.
    //   • enabled              → Entegrasyonu aç/kapat (OFF ise sadece modal'ı kapat)
    //   • sizeSelector         → Beden butonlarının CSS selector'ı. Class veya
    //                            attribute bazlı tanımlanabilir. Örn:
    //                            `.size-btn` veya `[data-sayz-size]`
    //   • sizeAttribute        → Beden değerini okuyacağımız attribute ADI
    //                            (selector değil!). Boş bırakılırsa butonun
    //                            `textContent`'i kullanılır.
    //   • outOfStockSelector   → Stokta OLMAYAN butonlarda matcheden CSS
    //                            selector — class (`.sold-out`), attribute
    //                            (`[data-stock="0"]`), ya da birleşik
    //                            (`button.disabled[aria-disabled="true"]`).
    //                            `el.matches(sel)` üzerinden kontrol edilir.
    //                            Boş bırakılırsa tüm bedenler stokta kabul
    //                            edilir. (Shopify/Woo/Ticimax deseni:
    //                            `.disabled`, `.sold-out`, `[data-stock="0"]`,
    //                            `[aria-disabled="true"]`)
    siteIntegration: {
        enabled: false,
        sizeSelector: "[data-sayz-size]",
        sizeAttribute: "data-sayz-size",
        outOfStockSelector: "",
    },
};

/**
 * Deep-merge a partial wizard settings object with defaults.
 * Handles the two nested copy sections + bodyPrompts + smartRecall.
 * Legacy `openingStyle` / `drawerDesktop` / `drawerMobile` fields are
 * silently dropped — the wizard is modal-only.
 */
function mergeSettings(base, patch) {
    if (!patch || typeof patch !== "object") return base;
    // Strip legacy drawer-related keys that may still exist in persisted docs
    // from the pre-MVP period.
    const { openingStyle: _os, drawerDesktop: _dd, drawerMobile: _dm, ...sanitized } = patch;
    return {
        ...base,
        ...sanitized,
        smartRecall: { ...(base.smartRecall || {}), ...(sanitized.smartRecall || {}) },
        stepCopy: {
            ...base.stepCopy,
            ...(sanitized.stepCopy || {}),
            bodyPrompts: {
                ...(base.stepCopy?.bodyPrompts || {}),
                ...(sanitized.stepCopy?.bodyPrompts || {}),
            },
            bodyHints: {
                ...(base.stepCopy?.bodyHints || {}),
                ...(patch.stepCopy?.bodyHints || {}),
            },
        },
        resultCopy: { ...base.resultCopy, ...(patch.resultCopy || {}) },
        siteIntegration: { ...(base.siteIntegration || {}), ...(sanitized.siteIntegration || {}) },
    };
}

/**
 * Merge wizard settings into the sizing_panel blocks array under the
 * find_my_size block's content.wizard field. If the block is missing,
 * append it with defaults so the panel keeps rendering it.
 */
function mergeWizardIntoBlocks(blocks, wizard) {
    const hasFms = blocks.some((b) => b.key === "find_my_size");
    if (!hasFms) {
        return [
            ...blocks,
            {
                key: "find_my_size",
                enabled: true,
                order: blocks.length,
                content: {
                    title: "Bedenimi Bul",
                    description: "Kendi bedeninizi 30 saniyede bulun.",
                    buttonText: "Bedenimi Bul",
                    modalTitle: "Bedenini Bul",
                    subtitle: "30 saniyede sana en uyacak bedeni bulalım.",
                    askAge: true,
                    wizard,
                },
            },
        ];
    }
    return blocks.map((b) => {
        if (b.key !== "find_my_size") return b;
        return {
            ...b,
            content: { ...(b.content || {}), wizard },
        };
    });
}

export default function FindMySizeWizard() {
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(DEFAULTS);
    const [accent, setAccent] = useState("#C86B4B");
    const [modalTitle, setModalTitle] = useState(DEFAULT_TITLE);
    const [tab, setTab] = useState("content");
    // Sayz Widget sayfasında merchant'ın seçtiği template + tokens — Nazik Ret
    // CTA'sı gibi wizard içi Sayz Widget stil'ine gönderme yapan yerler için.
    const [widgetTemplate, setWidgetTemplate] = useState("minimal");
    const [widgetTokens, setWidgetTokens] = useState({ accent: "#C86B4B" });
    // Sayz Widget "Beden Tablosu" block button metni — Nazik Ret CTA'sı bu
    // metni miras alır. Merchant Sayz Widget sayfasında değiştirdiğinde
    // otomatik güncellenir.
    const [sizeTableButtonText, setSizeTableButtonText] = useState("Beden Tablosu");

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const data = await fetchAllWidgetSettings();
                if (!alive) return;
                const w = data?.sizing_panel || {};
                const c = w.config || {};
                if (c?.tokens?.accent) setAccent(c.tokens.accent);
                if (c?.template) setWidgetTemplate(c.template);
                if (c?.tokens) setWidgetTokens((prev) => ({ ...prev, ...c.tokens }));
                const fms = Array.isArray(c.blocks) && c.blocks.find((b) => b.key === "find_my_size");
                const btn = fms?.content?.buttonText?.trim();
                if (btn) setModalTitle(btn);
                const sizeTable = Array.isArray(c.blocks) && c.blocks.find((b) => b.key === "size_table");
                const sizeTableBtn = sizeTable?.content?.buttonText?.trim();
                if (sizeTableBtn) setSizeTableButtonText(sizeTableBtn);
                // Hydrate wizard settings from block.content.wizard (source of truth on backend).
                // Backward-compat: if not present, seed once from any legacy localStorage payload.
                const remoteWizard = fms?.content?.wizard;
                if (remoteWizard && typeof remoteWizard === "object") {
                    setSettings((prev) => mergeSettings(prev, remoteWizard));
                } else {
                    try {
                        const saved = localStorage.getItem(STORAGE_KEY);
                        if (saved) {
                            const parsed = JSON.parse(saved);
                            setSettings((prev) => mergeSettings(prev, parsed));
                        }
                    } catch { /* ignore */ }
                }
            } catch {
                if (alive) toast.error("Sihirbaz ayarları yüklenemedi");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const update = (patch) => setSettings((s) => ({ ...s, ...patch }));
    const updateCopy = (section, key, value) =>
        setSettings((s) => ({ ...s, [section]: { ...s[section], [key]: value } }));
    const updateBodyPrompt = (kind, val) => {
        setSettings((s) => ({
            ...s,
            stepCopy: {
                ...s.stepCopy,
                bodyPrompts: { ...(s.stepCopy?.bodyPrompts || {}), [kind]: val },
            },
        }));
    };
    const updateBodyHint = (kind, val) => {
        setSettings((s) => ({
            ...s,
            stepCopy: {
                ...s.stepCopy,
                bodyHints: { ...(s.stepCopy?.bodyHints || {}), [kind]: val },
            },
        }));
    };
    const updateSmartRecall = (patch) =>
        setSettings((s) => ({ ...s, smartRecall: { ...(s.smartRecall || {}), ...patch } }));
    const updateSiteIntegration = (patch) =>
        setSettings((s) => ({ ...s, siteIntegration: { ...(s.siteIntegration || {}), ...patch } }));

    const handleSave = async () => {
        setSaving(true);
        try {
            // Read-mutate-write: fetch latest panel config, merge wizard into
            // find_my_size block, PUT full config back. Prevents overwriting
            // panel-level settings edited on the SayzWidget page.
            const data = await fetchAllWidgetSettings();
            const w = data?.sizing_panel || {};
            const currentConfig = w.config || {};
            const currentBlocks = Array.isArray(currentConfig.blocks) ? currentConfig.blocks : [];
            const nextConfig = {
                ...currentConfig,
                blocks: mergeWizardIntoBlocks(currentBlocks, settings),
            };
            await updateWidgetSettings("sizing_panel", { config: nextConfig });
            // Cache locally as offline mirror (harmless).
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
            toast.success("Sihirbaz ayarları kaydedildi");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    const smartEnabled = settings.smartRecall?.enabled !== false;
    const fastPathMode = settings.smartRecall?.fastPathMode || "auto_recommend";
    const kindRefusalOn = settings.outOfRangeStrategy === "kind_refusal";

    return (
        <div className="space-y-6" data-testid="find-my-size-wizard-page">
            <PageHeader
                icon={Sparkles}
                title="Bedenimi Bul"
                description="Müşteri 'Bedenimi Bul' CTA'sına tıkladığında açılacak sihirbazın adımlarını, algoritma davranışını ve sonuç kartını buradan yönetirsiniz. Tema ve renk widget'tan otomatik miras alınır."
                actions={
                    <PrimaryButton onClick={handleSave} disabled={saving || loading} data-testid="find-my-size-wizard-save-btn">
                        {saving ? (
                            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Kaydediliyor</>
                        ) : (
                            <><Save className="h-3.5 w-3.5" /> Kaydet</>
                        )}
                    </PrimaryButton>
                }
            />

            {loading ? (
                <div className="flex items-center justify-center py-24 text-muted-foreground" data-testid="fms-loading">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Yükleniyor…
                </div>
            ) : (
            <Tabs value={tab} onValueChange={setTab} data-testid="fms-tabs">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* SOL — 2-tab yapısı (Beden Tablosu ile senkron) */}
                    <div className="lg:col-span-7">
                        <div className="rounded-xl border border-border bg-background overflow-hidden">
                            <div className="px-5 pt-5 pb-3 border-b border-border bg-white">
                                <TabsList className={TAB_LIST_CLS}>
                                    <TabsTrigger value="content" className={TAB_TRIGGER_CLS} data-testid="fms-tab-content">
                                        <Layers className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        <span>İçerik</span>
                                    </TabsTrigger>
                                    <TabsTrigger value="smart" className={TAB_TRIGGER_CLS} data-testid="fms-tab-smart">
                                        <Wand2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        <span>Akıllı Katmanlar</span>
                                    </TabsTrigger>
                                    <TabsTrigger value="integration" className={TAB_TRIGGER_CLS} data-testid="fms-tab-integration">
                                        <Code2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                                        <span>Site Entegrasyonu</span>
                                    </TabsTrigger>
                                </TabsList>
                            </div>

                            <div className="p-4">
                                <TabsContent value="content" className="mt-0 space-y-4">
                                    <p className="text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="fms-tab-info-content">
                                        Karşılama, ölçü adımları ve final ekranlarının metinlerini buradan yönet. Davranış ayarları &quot;Akıllı Katmanlar&quot; sekmesinde.
                                    </p>

                                    <Card>
                                        <CardHeader
                                            title="Ekran Metinleri"
                                            desc="Sihirbaz adımlarında ziyaretçinin gördüğü tüm başlık, açıklama ve CTA metinleri."
                                        />
                                        <div className="p-4 pt-3">
                                            <div className="rounded-lg border border-border bg-background overflow-hidden">
                                                <Accordion type="multiple" className="w-full" data-testid="fms-config-accordion">
                                                    {/* ═══════════════════════════════════════════════
                                                      * 1) KARŞILAMA
                                                      * ═══════════════════════════════════════════════ */}
                                                    <SectionItem
                                                        value="welcome"
                                                        icon={Sparkle}
                                                        title="Karşılama"
                                                        desc="Karşılama ekranı metinlerini yönet."
                                                        testId="fms-section-welcome"
                                                    >
                                                        <WelcomeSection
                                                            settings={settings}
                                                            smartEnabled={smartEnabled}
                                                            fastPathMode={fastPathMode}
                                                            onCopyChange={updateCopy}
                                                        />
                                                    </SectionItem>

                                                    {/* ═══════════════════════════════════════════════
                                                      * 2) ÖLÇÜLER
                                                      * ═══════════════════════════════════════════════ */}
                                                    <SectionItem
                                                        value="measurements"
                                                        icon={Ruler}
                                                        title="Ölçüler"
                                                        desc="Temel bilgi, fit tercihi ve siluet ekranlarının metinleri."
                                                        testId="fms-section-measurements"
                                                    >
                                                        <MeasurementsSection
                                                            settings={settings}
                                                            onCopyChange={updateCopy}
                                                            onBodyPrompt={updateBodyPrompt}
                                                            onBodyHint={updateBodyHint}
                                                        />
                                                    </SectionItem>

                                                    {/* ═══════════════════════════════════════════════
                                                      * 3) FİNAL
                                                      * ═══════════════════════════════════════════════ */}
                                                    <SectionItem
                                                        value="final"
                                                        icon={Flag}
                                                        title="Final"
                                                        desc="Sonuç ve Nazik Ret ekranlarının metinleri."
                                                        testId="fms-section-final"
                                                        isLast
                                                    >
                                                        <FinalSection
                                                            settings={settings}
                                                            kindRefusalOn={kindRefusalOn}
                                                            onCopyChange={updateCopy}
                                                        />
                                                    </SectionItem>
                                                </Accordion>
                                            </div>
                                        </div>
                                    </Card>
                                </TabsContent>

                                <TabsContent value="smart" className="mt-0 space-y-4">
                                    <p className="text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="fms-tab-info-smart">
                                        Sihirbazın davranışını yöneten akıllı katmanlar. Ziyaretçinin nasıl karşılanacağı ve uygun beden bulunamadığında ne yapılacağı burada belirlenir.
                                    </p>
                                    <Card>
                                        <CardHeader
                                            title="Sihirbaz Davranışı"
                                            desc="Karşılama akışını ve uygun beden bulunamadığında sihirbazın nasıl davranacağını yönet."
                                        />
                                        <div className="px-4 py-2 divide-y divide-border/60" data-testid="fms-settings-card">
                                            <SettingRow
                                                icon={Sparkle}
                                                title="Akıllı Beden Önerisi"
                                                desc="Daha önce wizard'ı tamamlamış ziyaretçiyi hızlı yoldan karşıla — bilinen bilgilerle direkt öneri göster ya da eksikleri sor."
                                                hint="Kapatırsan hiç kimseye karşılama gösterilmez; herkes tam wizard'ı doldurur."
                                                checked={smartEnabled}
                                                onCheckedChange={(v) => updateSmartRecall({ enabled: v })}
                                                testId="fms-toggle-smart-recall"
                                            >
                                                {smartEnabled && (
                                                    <FastPathModePicker
                                                        value={fastPathMode}
                                                        onChange={(v) => updateSmartRecall({ fastPathMode: v })}
                                                    />
                                                )}
                                            </SettingRow>
                                            <SettingRow
                                                icon={Heart}
                                                title="Nazik Ret"
                                                desc="Ürünün beden aralığı dışında kalan ziyaretçilere yanlış beden önermek yerine nazikçe durumu bildir ve Beden Tablosu'na yönlendir."
                                                hint="Kapalıysa: en yakın bedeni yine de öneririz. İade riskini önemsiyorsan açık tut."
                                                checked={kindRefusalOn}
                                                onCheckedChange={(v) => update({ outOfRangeStrategy: v ? "kind_refusal" : "closest" })}
                                                testId="fms-toggle-kind-refusal"
                                            />
                                        </div>
                                    </Card>
                                </TabsContent>

                                <TabsContent value="integration" className="mt-0 space-y-4">
                                    <p className="text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="fms-tab-info-integration">
                                        &quot;Bedeni Seç&quot; butonuna tıklandığında widget, sitendeki beden butonunu otomatik seçsin ve stok durumunu okusun. Bu ayarlar sitenin HTML yapısına göre bir kerelik tanımlanır — e-ticaret altyapınla uyumlu olacak şekilde.
                                    </p>
                                    <Card>
                                        <CardHeader
                                            title="Site DOM Entegrasyonu"
                                            desc="Site üzerindeki beden butonlarını tanımlayıp otomatik tıklama ve stok okuma davranışını yönet."
                                        />
                                        <div className="px-4 py-2 divide-y divide-border/60" data-testid="fms-integration-card">
                                            <SettingRow
                                                icon={PackageCheck}
                                                title="Entegrasyon Aktif"
                                                desc="Açıkken: Önerilen beden stokta ise 'Bedeni Seç' tıklanınca sitendeki ilgili beden butonu otomatik seçilir; stokta değilse uyarı gösterilir. Kapalıyken: Yalnızca modal kapanır, hiçbir DOM müdahalesi yapılmaz."
                                                hint="İlk kurulumda kapalı tut, selector'ları doğrulayınca aç."
                                                checked={settings.siteIntegration?.enabled === true}
                                                onCheckedChange={(v) => updateSiteIntegration({ enabled: v })}
                                                testId="fms-toggle-integration-enabled"
                                            />
                                        </div>
                                        <div className="p-4 pt-3 space-y-4">
                                            <TextField
                                                label="Beden Butonları Selector"
                                                hint="Sitendeki tüm beden seçeneklerini bulan CSS selector. Class veya attribute kullanabilirsin."
                                                value={settings.siteIntegration?.sizeSelector || ""}
                                                onChange={(v) => updateSiteIntegration({ sizeSelector: v })}
                                                testId="fms-integration-selector"
                                            />
                                            <div className="-mt-3 rounded-md border border-border/60 bg-muted/20 p-2.5 text-[10.5px] text-muted-foreground leading-snug" data-testid="fms-integration-selector-examples">
                                                <div className="font-semibold text-foreground text-[11px] mb-1">Nasıl yazılır?</div>
                                                <div className="font-mono space-y-0.5">
                                                    <div>Class ile: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">.product-size-btn</code></div>
                                                    <div>Attribute ile: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">[data-size]</code></div>
                                                    <div>Birleşik: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">button.size-opt[data-value]</code></div>
                                                </div>
                                            </div>
                                            <TextField
                                                label="Beden Değeri Attribute"
                                                hint="Beden değerini okuyacağımız attribute ADI (selector değil). Boş bırakılırsa butonun içindeki yazı okunur — iç HTML etiketleri (span, bold vb.) de desteklenir, tüm metin birleştirilir."
                                                value={settings.siteIntegration?.sizeAttribute || ""}
                                                onChange={(v) => updateSiteIntegration({ sizeAttribute: v })}
                                                testId="fms-integration-attribute"
                                            />
                                            <div className="-mt-3 rounded-md border border-border/60 bg-muted/20 p-2.5 text-[10.5px] text-muted-foreground leading-snug" data-testid="fms-integration-attribute-examples">
                                                <div className="font-semibold text-foreground text-[11px] mb-1">Nasıl yazılır?</div>
                                                <div className="font-mono space-y-0.5">
                                                    <div>Normal: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">data-size</code> → okur: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">{'<button data-size="M">'}</code></div>
                                                    <div>Standart: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">value</code> → okur: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">{'<button value="M">'}</code></div>
                                                    <div>Boş bırak → düz metin: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">{'<button>M</button>'}</code></div>
                                                    <div>Boş bırak → iç etiket: <code className="text-[10.5px] px-1 py-0.5 rounded bg-background">{'<button><span>M</span></button>'}</code></div>
                                                </div>
                                            </div>
                                            <TextField
                                                label="Stokta Olmayan CSS Seçici (opsiyonel)"
                                                hint="Stokta OLMAYAN butonlarla eşleşen CSS seçici. Class (.sold-out), attribute ([data-stock='0']) ya da birleşik yazabilirsin. Boş bırakırsan tüm bedenler stokta kabul edilir."
                                                value={settings.siteIntegration?.outOfStockSelector || ""}
                                                onChange={(v) => updateSiteIntegration({ outOfStockSelector: v })}
                                                testId="fms-integration-oos-selector"
                                            />
                                        </div>
                                    </Card>
                                </TabsContent>
                            </div>
                        </div>
                    </div>

                    {/* SAĞ — Sticky Preview */}
                    <div className="lg:col-span-5">
                        <div className="sticky top-20 space-y-3">
                            <WizardPreviewProvider
                                accent={accent}
                                settings={settings}
                                modalTitle={modalTitle}
                                widgetTemplate={widgetTemplate}
                                widgetTokens={widgetTokens}
                                sizeTableButtonText={sizeTableButtonText}
                            >
                                <Card>
                                    <CardHeader
                                        title="Önizleme"
                                        desc="Ziyaretçi bu şekilde görecek."
                                    />
                                    <div className="p-4 bg-background rounded-b-xl">
                                        <WizardPreviewModal />
                                    </div>
                                </Card>
                                {/* Merchant-only step selector — lives OUTSIDE the preview
                                    Card so the tabs read as a panel-level control, not part
                                    of the wizard chrome. Never shipped to live. */}
                                <div className="px-1">
                                    <WizardPreviewNavigator />
                                </div>
                            </WizardPreviewProvider>
                        </div>
                    </div>
                </div>
            </Tabs>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════
 * ANA BÖLÜM (Accordion Item wrapper) — 3 üst düzey bölüm için
 *   • Sol tarafta ikon + başlık + kısa açıklama
 *   • Trigger sağa hizalı chevron
 *   • Son öğe hariç alt kenar (border-b) korunur
 *   • Trigger + AccordionContent bg-white (siyah bg-background ile karışmaz)
 * ═══════════════════════════════════════════════════════════════ */
const SectionItem = ({ value, icon: Icon, title, desc, testId, isLast, children }) => (
    <AccordionItem
        value={value}
        className={`bg-white ${isLast ? "border-b-0" : ""}`}
        data-testid={testId}
    >
        <AccordionTrigger className="px-5 py-4 hover:no-underline group bg-white" data-testid={`${testId}-trigger`}>
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <span className="h-9 w-9 shrink-0 rounded-md bg-muted text-muted-foreground group-hover:bg-foreground group-hover:text-background grid place-items-center transition-colors">
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1 text-left">
                    <div
                        className="text-[13px] font-semibold text-foreground"
                        dangerouslySetInnerHTML={{ __html: title }}
                    />
                    <div className="text-[11.5px] text-muted-foreground mt-0.5 leading-snug">
                        {desc}
                    </div>
                </div>
            </div>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 pt-1 bg-white">
            <div className="space-y-4">{children}</div>
        </AccordionContent>
    </AccordionItem>
);

/* ═══════════════════════════════════════════════════════════════
 * NESTED ACCORDION ITEM — alt kart benzeri, kompakt trigger
 *   • Kart görünümünü accordion içine gömüyoruz — birebir Card benzeri
 *   • disabled prop'u ile "erişilebilir ama görünür" pattern destekli
 * ═══════════════════════════════════════════════════════════════ */
const SubItem = ({ value, icon: Icon, title, desc, disabled, children, testId }) => (
    <AccordionItem
        value={value}
        className={`rounded-lg border border-border bg-surface overflow-hidden ${disabled ? "opacity-50" : ""}`}
        disabled={disabled}
        data-testid={testId}
    >
        <AccordionTrigger
            disabled={disabled}
            className="px-3.5 py-3 hover:no-underline group disabled:cursor-not-allowed"
            data-testid={`${testId}-trigger`}
        >
            <div className="flex items-start gap-2.5 flex-1 min-w-0">
                {Icon && (
                    <span className="h-7 w-7 shrink-0 rounded-md bg-background text-muted-foreground grid place-items-center border border-border">
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </span>
                )}
                <div className="min-w-0 flex-1 text-left">
                    <div className="text-[12.5px] font-semibold text-foreground">{title}</div>
                    {desc && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{desc}</div>
                    )}
                </div>
            </div>
        </AccordionTrigger>
        <AccordionContent className="px-3.5 pb-3.5 pt-1">
            <div className="space-y-3.5">{children}</div>
        </AccordionContent>
    </AccordionItem>
);

/* ═══════════════════════════════════════════════════════════════
 * SETTING ROW — Kompakt ayar satırı, tam bir "settings UI" hissi:
 *   [ikon] [başlık + açıklama]                              [Switch]
 *   Border yok, divider ile ayrılır. Accordion trigger görünmez.
 *   children — koşullu alt kontrol (ör: FastPathModePicker).
 * ═══════════════════════════════════════════════════════════════ */
const SettingRow = ({ icon: Icon, title, desc, hint, checked, onCheckedChange, testId, children }) => (
    <div className="py-3.5" data-testid={`${testId}-row`}>
        <div className="flex items-start gap-3">
            {Icon && (
                <span className="h-9 w-9 shrink-0 rounded-md bg-muted text-muted-foreground grid place-items-center mt-0.5">
                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                </span>
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-[13px] font-semibold text-foreground">{title}</div>
                    <Switch
                        checked={checked}
                        onCheckedChange={onCheckedChange}
                        data-testid={testId}
                    />
                </div>
                <div className="text-[11.5px] text-muted-foreground mt-1 leading-snug">{desc}</div>
                {hint && (
                    <div className="text-[10.5px] text-muted-foreground/70 mt-1 leading-snug italic">{hint}</div>
                )}
                {children && <div className="mt-3">{children}</div>}
            </div>
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════════════════
 * FAST PATH MODE PICKER — Akıllı Beden Önerisi ON iken görünen
 *   yatay seçim çifti. Kompakt segmented-control tarzı.
 * ═══════════════════════════════════════════════════════════════ */
const FAST_PATH_MODES = [
    {
        value: "auto_recommend",
        icon: Zap,
        label: "Otomatik Öneri",
        desc: "Ziyaretçi karşılamada direkt önerdiğimiz bedeni ve tek CTA'yı görür. Sürtünme minimum, dönüşüm önceliklidir.",
    },
    {
        value: "always_confirm",
        icon: Repeat,
        label: "Önce Onay Al",
        desc: "Profil tam olsa bile 'Bilgilerin hâlâ doğru mu?' diye önce onay isteriz, sonra öneriye geçeriz.",
    },
];

const FastPathModePicker = ({ value, onChange }) => (
    <div className="grid grid-cols-1 gap-2" data-testid="fms-fast-path-modes">
        {FAST_PATH_MODES.map((opt) => {
            const selected = value === opt.value;
            const Icon = opt.icon;
            return (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    className={`w-full flex items-start gap-3 rounded-lg border p-3 text-left transition-all ${
                        selected
                            ? "border-foreground bg-foreground/[0.03]"
                            : "border-border bg-surface hover:border-foreground/30"
                    }`}
                    data-testid={`fms-fast-path-${opt.value}`}
                    aria-pressed={selected}
                >
                    <span className={`h-7 w-7 shrink-0 grid place-items-center rounded-md ${
                        selected ? "bg-foreground text-background" : "bg-muted text-muted-foreground"
                    }`}>
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="flex items-center justify-between gap-2">
                            <div className="text-[12.5px] font-semibold text-foreground">{opt.label}</div>
                            <span className={`h-3.5 w-3.5 rounded-full border-2 shrink-0 ${
                                selected ? "border-foreground bg-foreground" : "border-border"
                            }`} />
                        </div>
                        <div className="text-[11px] text-muted-foreground leading-snug">{opt.desc}</div>
                    </div>
                </button>
            );
        })}
    </div>
);

/* ═══════════════════════════════════════════════════════════════
 * TEXT FIELD — Ortak metin girişi (paylaşılan)
 * ═══════════════════════════════════════════════════════════════ */
const TextField = ({ label, hint, value, onChange, testId, multiline }) => (
    <div className="space-y-1.5">
        <label className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
        </label>
        {multiline ? (
            <Textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                rows={2}
                className="text-[13px] bg-background resize-none min-h-[60px]"
                data-testid={testId}
            />
        ) : (
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-10 text-[13px] bg-background"
                data-testid={testId}
            />
        )}
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
);

/* ═══════════════════════════════════════════════════════════════
 * KARŞILAMA
 *   ▸ Nested: Karşılama · Tam (fastPathMode'a göre input seti)
 *   ▸ Nested: Karşılama · Kısmi Profil
 *   Smart OFF: nested'ler görünür ama disabled (opacity 50).
 *   Master toggle + fastPath radio artık üstteki Ayarlar Kartı'nda.
 * ═══════════════════════════════════════════════════════════════ */
const WelcomeSection = ({ settings, smartEnabled, fastPathMode, onCopyChange }) => (
    <Accordion type="multiple" className="w-full space-y-2" data-testid="fms-welcome-nested">
        <SubItem
            value="welcome-full"
            icon={Sparkle}
            title="Karşılama · Tam"
            desc="Ziyaretçinin tüm bilgileri bilindiğinde gösterilir. Hızlı yol davranışına göre metinler değişir."
            disabled={!smartEnabled}
            testId="fms-sub-welcome-full"
        >
            {fastPathMode === "always_confirm" ? (
                <>
                    <TextField label="Etiket" hint="Küçük vurgu etiketi (ikonun yanında)." value={settings.stepCopy?.welcomeConfirmLabel || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeConfirmLabel", v)} testId="fms-copy-welcome-confirm-label" />
                    <TextField label="Başlık" value={settings.stepCopy?.welcomeConfirmTitle || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeConfirmTitle", v)} testId="fms-copy-welcome-confirm-title" />
                    <TextField label="Açıklama" value={settings.stepCopy?.welcomeConfirmDesc || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeConfirmDesc", v)} testId="fms-copy-welcome-confirm-desc" multiline />
                    <TextField label="Onay Butonu" hint="Öneriye geçiş için ana CTA." value={settings.stepCopy?.welcomeConfirmButton || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeConfirmButton", v)} testId="fms-copy-welcome-confirm-btn" />
                    <TextField label="Değiştir Linki" hint="Ziyaretçi bilgilerini değiştirmek isterse gösterilen ikincil link." value={settings.stepCopy?.welcomeChange || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeChange", v)} testId="fms-copy-welcome-change-1" />
                </>
            ) : (
                <>
                    <TextField label="Etiket" hint="Küçük vurgu etiketi (ikonun yanında)." value={settings.stepCopy?.welcomeLabel || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeLabel", v)} testId="fms-copy-welcome-label" />
                    <TextField label="Başlık" value={settings.stepCopy?.welcomeTitle || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeTitle", v)} testId="fms-copy-welcome-title" />
                    <TextField label="Açıklama" value={settings.stepCopy?.welcomeDesc || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeDesc", v)} testId="fms-copy-welcome-desc" multiline />
                    <TextField label="Devam Butonu" hint="Öneri gösterildiğinde ana CTA." value={settings.stepCopy?.welcomeContinueButton || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeContinueButton", v)} testId="fms-copy-welcome-continue-btn" />
                    <TextField label="Değiştir Linki" hint="Ziyaretçi bilgilerini değiştirmek isterse gösterilen ikincil link." value={settings.stepCopy?.welcomeChange || ""} onChange={(v) => onCopyChange("stepCopy", "welcomeChange", v)} testId="fms-copy-welcome-change" />
                </>
            )}
        </SubItem>

        <SubItem
            value="welcome-partial"
            icon={HelpCircle}
            title="Karşılama · Kısmi"
            desc="Bazı bilgiler var ama ürünün istediği bir alan eksikse gösterilir. Eksik soruyla tek ekranda."
            disabled={!smartEnabled}
            testId="fms-sub-welcome-partial"
        >
            <TextField label="Etiket" value={settings.stepCopy?.welcomePartialLabel || ""} onChange={(v) => onCopyChange("stepCopy", "welcomePartialLabel", v)} testId="fms-copy-welcome-partial-label" />
            <TextField label="Başlık" value={settings.stepCopy?.welcomePartialTitle || ""} onChange={(v) => onCopyChange("stepCopy", "welcomePartialTitle", v)} testId="fms-copy-welcome-partial-title" />
            <TextField label="Açıklama" value={settings.stepCopy?.welcomePartialDesc || ""} onChange={(v) => onCopyChange("stepCopy", "welcomePartialDesc", v)} testId="fms-copy-welcome-partial-desc" multiline />
        </SubItem>
    </Accordion>
);

/* ═══════════════════════════════════════════════════════════════
 * 3) ÖLÇÜLER
 *   ▸ Nested: Temel, Fit, Göğüs, Bel, Kalça, Omuz — her siluet için
 *     ayrı "Başlık" ve "İpucu Metni" alanı (artık ortak değil).
 * ═══════════════════════════════════════════════════════════════ */
const BODY_KINDS = [
    { kind: "chest",    icon: Shirt,              title: "Göğüs", desc: "Ürünün göğüs ölçüsü etiketi olduğunda gösterilir." },
    { kind: "waist",    icon: MoveHorizontal,     title: "Bel",   desc: "Ürünün bel ölçüsü etiketi olduğunda gösterilir." },
    { kind: "hip",      icon: Grip,               title: "Kalça", desc: "Ürünün kalça ölçüsü etiketi olduğunda gösterilir." },
    { kind: "shoulder", icon: StretchHorizontal,  title: "Omuz",  desc: "Ürünün omuz ölçüsü etiketi olduğunda gösterilir." },
];

const MeasurementsSection = ({ settings, onCopyChange, onBodyPrompt, onBodyHint }) => (
    <div className="space-y-4">
        <Accordion type="multiple" className="w-full space-y-2" data-testid="fms-measurements-nested">
            <SubItem
                value="basic"
                icon={Layers}
                title="Temel"
                desc="Boy + Kilo + Yaş sorulan ilk adım."
                testId="fms-sub-basic"
            >
                <TextField label="Başlık" value={settings.stepCopy?.basicTitle || ""} onChange={(v) => onCopyChange("stepCopy", "basicTitle", v)} testId="fms-copy-basic-title" />
                <TextField label="Açıklama" value={settings.stepCopy?.basicDesc || ""} onChange={(v) => onCopyChange("stepCopy", "basicDesc", v)} testId="fms-copy-basic-desc" multiline />
            </SubItem>

            <SubItem
                value="fit"
                icon={Move3d}
                title="Fit"
                desc="Tam / Rahat / Bol seçimi. Seçim yapılınca otomatik ilerler."
                testId="fms-sub-fit"
            >
                <TextField label="Başlık" value={settings.stepCopy?.fitTitle || ""} onChange={(v) => onCopyChange("stepCopy", "fitTitle", v)} testId="fms-copy-fit-title" />
                <TextField label="Açıklama" value={settings.stepCopy?.fitDesc || ""} onChange={(v) => onCopyChange("stepCopy", "fitDesc", v)} testId="fms-copy-fit-desc" multiline />
            </SubItem>

            {BODY_KINDS.map(({ kind, icon, title, desc }) => (
                <SubItem
                    key={kind}
                    value={kind}
                    icon={icon}
                    title={title}
                    desc={desc}
                    testId={`fms-sub-${kind}`}
                >
                    <TextField
                        label="Başlık"
                        value={settings.stepCopy?.bodyPrompts?.[kind] || ""}
                        onChange={(v) => onBodyPrompt(kind, v)}
                        testId={`fms-copy-body-${kind}`}
                    />
                    <TextField
                        label="İpucu Metni"
                        hint="Başlığın altında görünen küçük yardımcı metin — her siluet için özel."
                        value={settings.stepCopy?.bodyHints?.[kind] || ""}
                        onChange={(v) => onBodyHint(kind, v)}
                        testId={`fms-copy-body-hint-${kind}`}
                        multiline
                    />
                </SubItem>
            ))}
        </Accordion>
    </div>
);

/* ═══════════════════════════════════════════════════════════════
 * 4) FİNAL
 *   ▸ Nested: Sonuç (öneri ekranı metinleri)
 *   ▸ Nested (koşullu): Nazik Ret metinleri — sadece Nazik Ret
 *     toggle üstteki Ayarlar Kartı'nda ON iken görünür.
 * ═══════════════════════════════════════════════════════════════ */
const FinalSection = ({ settings, kindRefusalOn, onCopyChange }) => (
    <Accordion type="multiple" className="w-full space-y-2" data-testid="fms-final-nested">
        <SubItem
            value="result"
            icon={Sparkles}
            title="Sonuç"
            desc="Beden önerisi + güven skoru + Beden Tablosu köprüsü."
            testId="fms-sub-result"
        >
            <TextField label="Etiket" hint="Beden numarasının üstünde ince vurgu." value={settings.resultCopy?.resultLabel || ""} onChange={(v) => onCopyChange("resultCopy", "resultLabel", v)} testId="fms-copy-result-label" />
            <TextField label="Açıklama" value={settings.resultCopy?.resultText || ""} onChange={(v) => onCopyChange("resultCopy", "resultText", v)} testId="fms-copy-result-text" multiline />
            <TextField label="Beden Seç Butonu" value={settings.resultCopy?.selectSizeButton || ""} onChange={(v) => onCopyChange("resultCopy", "selectSizeButton", v)} testId="fms-copy-select-size-btn" />
        </SubItem>

        {kindRefusalOn && (
            <SubItem
                value="refusal"
                icon={Heart}
                title="Nazik Ret"
                desc="Beden aralığı dışında kalan ziyaretçiye gösterilen ekranın metinleri."
                testId="fms-sub-refusal"
            >
                <TextField label="Etiket" value={settings.resultCopy?.refusalLabel || ""} onChange={(v) => onCopyChange("resultCopy", "refusalLabel", v)} testId="fms-copy-refusal-label" />
                <TextField label="Açıklama" value={settings.resultCopy?.refusalText || ""} onChange={(v) => onCopyChange("resultCopy", "refusalText", v)} testId="fms-copy-refusal-text" multiline />
                <TextField label="Buton" hint="Bu Bedeni Seç yerine bu görünür. Sayz Widget 'Beden Tablosu' metnini miras alır." value={settings.resultCopy?.sizeChartButton || ""} onChange={(v) => onCopyChange("resultCopy", "sizeChartButton", v)} testId="fms-copy-size-chart-btn" />
            </SubItem>
        )}
    </Accordion>
);
